/* global WEB_ROOT */

import React, { PropTypes } from 'react';
import { Link } from 'react-router';

import ButtonLink from '../ButtonLink';
import Container from '../Container';
import EventListContainer from '../EventList/EventListContainer.react';
import Meta from '../../containers/Meta';
import MemberBenefits from '../MemberBenefits';
import OrganiserReviews from '../EventDetail/OrganiserReviews';
import ReadMore from '../ReadMore';
import SearchHeader from './components/SearchHeader';

import {
  benefits,
  breadcrumb,
  button,
  categoryList,
  container,
  content,
  exploreLink,
  exploreList,
  header,
  heading,
  layout,
  listContainer,
  more,
  subtitle,
  title as titleStyle,
} from './styles.module.css';

/**
 * Discovery Page presentational component.
 */
export const DiscoveryPage = ({
  allCategories,
  description,
  filters,
  image,
  label,
  lists,
  locale,
  localeSlug,
  meta,
  otherSports,
  parent,
  permalink,
  query,
  savedCount,
  searchLink,
  slug,
  title,
  topTips,
  upcomingCount,
  withLocation,
}) => {
  const showSearchHeader = !withLocation && permalink !== '/triathlon-events' && permalink !== '/triathlon-events/olympic';

  const defaultMeta = {
    title: `Find ${title}`,
    description: `Find upcoming local ${title} with Let’s Do This. We make it simple to search for local ${title}.`,
    socialImage: image.large,
    canonical: `${WEB_ROOT}${localeSlug ? `/${localeSlug}${permalink}` : permalink}`,
  };

  const currentLocale = localeSlug ? locale.find(l => l.slug === localeSlug) : undefined;
  const otherLocales = !withLocation && locale.filter(l => !!l.label && l.slug !== localeSlug);

  let metaTitle = meta.title || defaultMeta.title;
  if (localeSlug && !withLocation) {
    const article = ['gb', 'us'].includes(localeSlug) ? 'the' : '';
    metaTitle = `${metaTitle} ${article} in ${currentLocale.label}`;
  }

  const pageTitle = withLocation ? `${withLocation.name} ${title}` : `${localeSlug ? currentLocale.label : ''} ${title}`;

  return (
    <div className={layout}>
      <Meta
        {...defaultMeta}
        {...meta}
        title={`${metaTitle} — Book Now at Let's Do This`}
        useTitleTemplate={false}
        queries={lists.map(list => list.query)}
      />

      {showSearchHeader ? (
        <SearchHeader title={pageTitle} countryCode={localeSlug} query={query} />
      ) : (
        <header className={header} style={{ backgroundImage: `url(${image.large})` }}>
          <Container className={container}>
            <h1 className={titleStyle}>{pageTitle}</h1>
            <p className={subtitle}>
              {upcomingCount && `${upcomingCount} upcoming events`}
              {upcomingCount && savedCount && ', '}
              {savedCount && `saved by ${savedCount} users`}
            </p>
            <ButtonLink rel="nofollow" primary to={searchLink} className={button}>
              {'Show all'}
            </ButtonLink>
          </Container>
        </header>
      )}

      <Container className={container}>
        {slug !== 'all' && (
          <ol className={breadcrumb} vocab="http://schema.org/" typeof="BreadcrumbList">
            <li property="itemListElement" typeof="ListItem">
              <Link
                property="item"
                typeof="WebPage"
                to="/"
              >{'Home'}</Link>
              <meta property="name" content={'Home'} />
            </li>
            {parent && (
              <li property="itemListElement" typeof="ListItem">
                <Link
                  to={localeSlug ? `/${localeSlug}${parent.permalink}` : `${parent.permalink}`}
                  property="item"
                  typeof="WebPage"
                >{parent.label}</Link>
                <meta property="name" content={parent.label} />
              </li>
            )}
            <li property="itemListElement" typeof="ListItem">
              <Link
                to={localeSlug ? `/${localeSlug}${permalink}` : `${permalink}`}
                property="item"
                typeof="WebPage"
              >{label}</Link>
              <meta property="name" content={label} />
            </li>
          </ol>
        )}

        {otherLocales && (
          <ul style={{ display: 'none' }}>
            {otherLocales.map(l => (
              <li key={l.slug}>
                <Link to={`/${l.slug}${permalink}`}>{title} in {l.label}</Link>
              </li>
            ))}
          </ul>
        )}

        {filters && Object.keys(filters).length > 0 && (
          <div>
            <h3 className={heading}>{'Discover by category'}</h3>
            <ul className={categoryList}>
              {Object.keys(filters).map((key) => {
                const filter = filters[key];
                return (
                  <li key={filter.slug}>
                    <Link to={localeSlug ? `/${localeSlug}${filter.permalink}` : `${filter.permalink}`}>{filter.label}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {description && (
          <div>
            <h2 className={heading}>{`What you need to know about ${
                parent ? `${label} ${parent.label}` : label
              }`}</h2>
            <div className={content}>
              <ReadMore content={description} html />
            </div>
          </div>
        )}

        {slug === 'all' && allCategories && (
          <ul style={{ display: 'none' }}>
            {allCategories.slice(1).map(c => (
              <li key={c}>
                <Link to={c}>{c.slice(4).replace(/[-/]/g, ' ')}</Link>
              </li>
            ))}
          </ul>
        )}

        <div className={listContainer}>
          {lists.slice(0, 2).map(list => (
            <div key={list.title}>
              {list.permalink && (
                <Link to={localeSlug ? `/${localeSlug}${list.permalink}` : `${list.permalink}`} className={more}>
                  {'See all'}
                </Link>
              )}
              <h3 className={heading}>
                <Link to={list.searchLink} rel="nofollow">{list.title}</Link>
              </h3>
              <EventListContainer
                query={lists.length > 1 ? { ...list.query, resultsPerPage: 6 } : list.query}
                showLoadMore={false}
                source="category_page"
                carousel={lists.length > 1}
              />
              {lists.length === 1 && (
                <footer>
                  <ButtonLink rel="nofollow" primary to={list.searchLink}>
                    {'See more'}
                  </ButtonLink>
                </footer>
              )}
            </div>
          ))}
        </div>
      </Container>

      <div className={benefits}>
        <Container className={container}>
          <MemberBenefits columns />
        </Container>
      </div>

      <Container className={container}>
        {lists.length > 2 && (
          <div className={listContainer}>
            {lists.slice(2).map(list => (
              <div key={list.title}>
                {list.permalink && (
                  <Link to={localeSlug ? `/${localeSlug}${list.permalink}` : `${list.permalink}`} className={more}>
                    {'See all'}
                  </Link>
                )}
                <h3 className={heading}>
                  <Link rel="nofollow" to={list.searchLink}>{list.title}</Link>
                </h3>
                <EventListContainer
                  query={list.query}
                  showLoadMore={false}
                  source="category_page"
                  carousel
                />
              </div>
            ))}
          </div>
        )}

        {topTips && (
          <section>
            <Container className={container}>
              <h2 className={'headingFullWidth'}>{'Top tips'}</h2>
              <OrganiserReviews organiserReviews={topTips} reviewType="topTips" />
            </Container>
          </section>
        )}

        <h3 className={heading}>{'Discover by sport'}</h3>
        <ul className={exploreList}>
          {otherSports.map(sport => (
            <li key={sport.slug}>
              <Link
                to={localeSlug ? `/${localeSlug}${sport.permalink}` : `${sport.permalink}`}
                style={{ backgroundImage: `url(${sport.image})` }}
                className={exploreLink}
              >
                <span>{sport.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Container>
    </div>
  );
};

DiscoveryPage.propTypes = {
  allCategories: PropTypes.arrayOf(PropTypes.string.isRequired),
  description: PropTypes.string,
  filters: PropTypes.shape(),
  image: PropTypes.shape({
    large: PropTypes.string.isRequired,
  }).isRequired,
  label: PropTypes.string.isRequired,
  lists: PropTypes.arrayOf(
    PropTypes.shape({
      permalink: PropTypes.string,
      query: PropTypes.shape().isRequired,
      searchLink: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  locale: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired).isRequired,
  localeSlug: PropTypes.string,
  meta: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
  }),
  otherSports: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      permalink: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  parent: PropTypes.shape({
    label: PropTypes.string.isRequired,
    permalink: PropTypes.string.isRequired,
  }),
  permalink: PropTypes.string.isRequired,
  query: PropTypes.shape().isRequired,
  savedCount: PropTypes.string,
  searchLink: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  topTips: PropTypes.arrayOf(PropTypes.shape()),
  upcomingCount: PropTypes.string,
  withLocation: PropTypes.shape(),
};

DiscoveryPage.defaultProps = {
  allCategories: undefined,
  description: undefined,
  filters: undefined,
  localeSlug: undefined,
  meta: {},
  parent: undefined,
  savedCount: undefined,
  topTips: undefined,
  upcomingCount: undefined,
  withLocation: undefined,
};

export default DiscoveryPage;



// WEBPACK FOOTER //
// ./app/src/components/Discovery/component.jsx